import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FloatingControlShell } from '@vd/annotation-core/react';
import '@vd/annotation-core/floating-shell.css';
import {
  Bug,
  Camera,
  Check,
  CircleStop,
  Clapperboard,
  Lightbulb,
  Sparkles,
  RotateCcw,
} from 'lucide-react';
import type { DocumentPipSnapshot } from './DocumentPipController';
import {
  type RecordingSegmentController,
  type RecordingSegmentSnapshot,
} from './RecordingSegmentController';
import type { RecorderPhase } from './types';
import type { RecordingSnapshotUploadSnapshot } from './RecordingSnapshotController';
import './RecorderFloatingControl.css';

export function formatRecorderDuration(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1_000));
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remaining = seconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`
    : `${minutes}:${String(remaining).padStart(2, '0')}`;
}

function editableTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== 'function') return false;
  return Boolean((target as Element).closest(
    'input, textarea, select, [contenteditable="true"], [role="textbox"]',
  ));
}

function letterForShortcut(event: KeyboardEvent): string {
  const codeMatch = /^Key([A-Z])$/.exec(event.code);
  if (codeMatch?.[1]) return codeMatch[1].toLowerCase();
  return event.key.length === 1 ? event.key.toLowerCase() : '';
}

function snapshotFailureGuidance(code: string | undefined): string {
  switch (code) {
    case 'capture_forbidden_for_origin': return 'Blocked by capture policy';
    case 'payload_too_large':
    case 'snapshot_too_large': return 'Snapshot is too large';
    case 'snapshot_count_limit': return 'Snapshot limit reached';
    case 'snapshot_storage_limit': return 'Recording storage limit reached';
    case 'snapshot_request_conflict': return 'Moment no longer matches this segment';
    case 'snapshot_timeline_invalid': return 'Moment is outside the ticket timeline';
    case 'FREE_MONTHLY_CAP_REACHED': return 'Monthly workspace limit reached';
    default: return 'This snapshot cannot be retried';
  }
}

type RecorderFloatingControlProps = {
  controller: RecordingSegmentController;
  snapshot: RecordingSegmentSnapshot;
  elapsedMs: number;
  stopping: boolean;
  recorderPhase?: RecorderPhase;
  inPictureInPicture?: boolean;
  snapshotUploads?: RecordingSnapshotUploadSnapshot;
  onRetrySnapshot?: (key: string) => void;
};

export function RecorderFloatingControl({
  controller,
  snapshot,
  elapsedMs,
  stopping,
  recorderPhase = 'recording',
  inPictureInPicture = false,
  snapshotUploads = { ownerId: null, pending: 0, failed: 0, uploading: 0, items: [] },
  onRetrySnapshot,
}: RecorderFloatingControlProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const openSegment = snapshot.openSegment;
  const starting = !['recording', 'stopping'].includes(recorderPhase);
  const busy = starting || snapshot.phase === 'loading' || snapshot.phase === 'mutating';
  const retryableSettlements = Math.max(
    0,
    snapshot.pendingSettlements - snapshot.terminalSettlements - snapshot.cleanupSettlements,
  );

  useEffect(() => {
    if (openSegment) setChooserOpen(false);
  }, [openSegment]);

  useEffect(() => {
    if (!snapshot.notice) return;
    const timer = setTimeout(() => controller.dismissNotice(), 4_800);
    return () => clearTimeout(timer);
  }, [controller, snapshot.notice]);

  useEffect(() => {
    const document = rootRef.current?.ownerDocument;
    if (!document) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const key = letterForShortcut(event);
      if (event.repeat || editableTarget(event.target)) return;
      const controlHasFocus = Boolean(rootRef.current?.contains(document.activeElement));
      if (
        chooserOpen
        && controlHasFocus
        && !event.altKey
        && !event.ctrlKey
        && !event.metaKey
        && (key === 'b' || key === 'r')
      ) {
        event.preventDefault();
        void controller.startSegment(key === 'b' ? 'bug' : 'feature_request');
        return;
      }
      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      if (!['s', 'r', 'v', 'x'].includes(key)) return;
      event.preventDefault();
      if (key === 's') void controller.startSegment('bug');
      else if (key === 'r') void controller.startSegment('feature_request');
      else if (key === 'v') void controller.captureMoment();
      else void controller.finishSegment();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [chooserOpen, controller]);

  const startChooser = () => {
    if (openSegment) {
      void controller.startSegment('bug');
      return;
    }
    if (!busy) setChooserOpen((open) => !open);
  };

  const shell = (
    <FloatingControlShell
      storageKey={inPictureInPicture ? 'vd-recorder-pip-position' : 'vd-recorder-tab-position'}
      positionVersion={1}
      label="Toado walkthrough"
      icon={<Clapperboard />}
      badgeCount={openSegment?.snapshotCount ?? snapshot.segments.length}
      defaultCollapsed={false}
      offsetX={inPictureInPicture ? 10 : 22}
      offsetY={inPictureInPicture ? 10 : 22}
      storage={inPictureInPicture ? null : undefined}
      className="vd-recorder-control-shell"
      headerActions={(
        <span className="vd-recorder-control__live" data-state={starting ? 'starting' : 'live'}>
          <i /> {starting ? 'Starting' : 'Live'}
        </span>
      )}
    >
      <div ref={rootRef} className="vd-recorder-control" data-pip={inPictureInPicture ? 'true' : 'false'}>
        <div className="vd-recorder-control__clock">
          <span>{starting ? 'Preparing recorder' : 'Recording time'}</span>
          <strong>{formatRecorderDuration(elapsedMs)}</strong>
        </div>

        <div
          className="vd-recorder-control__status"
          data-segment={openSegment?.segmentType ?? 'idle'}
        >
          <span className="vd-recorder-control__status-icon">
            {openSegment?.segmentType === 'bug'
              ? <Bug />
              : openSegment?.segmentType === 'feature_request'
                ? <Lightbulb />
                : <Sparkles />}
          </span>
          <span>
            <small>{openSegment ? 'Now describing' : 'Ready for a ticket'}</small>
            <strong>
              {openSegment?.segmentType === 'bug'
                ? 'Bug'
                : openSegment?.segmentType === 'feature_request'
                  ? 'Feature request'
                  : `${snapshot.segments.length} ticket${snapshot.segments.length === 1 ? '' : 's'} marked`}
            </strong>
          </span>
          {openSegment && (
            <span className="vd-recorder-control__moment-count">
              <Camera /> {openSegment.snapshotCount}
            </span>
          )}
        </div>

        {snapshot.pendingSettlements > 0 && (
          <p
            className="vd-recorder-control__recovery"
            data-state={snapshot.terminalSettlements > 0
              ? 'terminal'
              : snapshot.cleanupSettlements > 0
                ? 'cleanup'
                : 'pending'}
            role={snapshot.terminalSettlements > 0 ? 'alert' : 'status'}
          >
            {snapshot.terminalSettlements > 0
              ? `${snapshot.terminalSettlements} saved ticket ${snapshot.terminalSettlements === 1 ? 'boundary needs' : 'boundaries need'} review`
              : snapshot.cleanupSettlements > 0
                ? `${snapshot.cleanupSettlements} ticket ${snapshot.cleanupSettlements === 1 ? 'boundary is' : 'boundaries are'} saved; local cleanup pending${retryableSettlements > 0 ? `, ${retryableSettlements} still syncing` : ''}`
                : `${snapshot.pendingSettlements} ticket ${snapshot.pendingSettlements === 1 ? 'boundary is' : 'boundaries are'} saved for retry`}
          </p>
        )}

        {snapshotUploads.items.filter((item) => item.state === 'failed').map((item) => (
          <div className="vd-recorder-control__snapshot-failure" role="alert" key={item.key}>
            <span>
              <small>{item.trigger === 'start' ? 'Opening snapshot' : item.trigger === 'finish' ? 'Finish snapshot' : 'Captured moment'}</small>
              <strong>Upload failed after {item.attempts} {item.attempts === 1 ? 'try' : 'tries'}</strong>
            </span>
            {item.retryable ? (
              <button type="button" onClick={() => onRetrySnapshot?.(item.key)} disabled={!onRetrySnapshot}>
                <RotateCcw /> Retry
              </button>
            ) : (
              <strong>{snapshotFailureGuidance(item.lastErrorCode)}</strong>
            )}
          </div>
        ))}

        {(snapshotUploads.pending > 0 || snapshotUploads.uploading > 0 || snapshotUploads.failed > 0) && (
          <p
            className="vd-recorder-control__snapshot-sync"
            data-state={snapshotUploads.failed > 0 ? 'failed' : 'syncing'}
            role={snapshotUploads.failed > 0 ? 'alert' : 'status'}
          >
            <Camera />
            {snapshotUploads.failed > 0
              ? `${snapshotUploads.failed} snapshot ${snapshotUploads.failed === 1 ? 'needs' : 'need'} attention`
              : `${snapshotUploads.pending + snapshotUploads.uploading} snapshot ${snapshotUploads.pending + snapshotUploads.uploading === 1 ? 'is' : 'are'} syncing`}
          </p>
        )}

        <div className="vd-recorder-control__actions" aria-busy={busy ? 'true' : 'false'}>
          <button
            type="button"
            className="vd-recorder-control__start"
            aria-expanded={chooserOpen}
            aria-disabled={openSegment ? 'true' : undefined}
            disabled={busy || stopping}
            onClick={startChooser}
          >
            <span><Sparkles /> Start describing ticket</span>
            <kbd>Alt S/R</kbd>
          </button>

          {chooserOpen && !openSegment && (
            <div className="vd-recorder-control__chooser" role="group" aria-label="Choose ticket type">
              <button
                type="button"
                disabled={busy || stopping}
                onClick={() => { void controller.startSegment('bug'); }}
              >
                <Bug />
                <span><strong>Bug</strong><small>Something is broken</small></span>
                <kbd>B</kbd>
              </button>
              <button
                type="button"
                disabled={busy || stopping}
                onClick={() => { void controller.startSegment('feature_request'); }}
              >
                <Lightbulb />
                <span><strong>Feature request</strong><small>Show what should exist</small></span>
                <kbd>R</kbd>
              </button>
            </div>
          )}

          <div className="vd-recorder-control__action-grid">
            <button
              type="button"
              aria-disabled={!openSegment ? 'true' : undefined}
              disabled={busy || stopping}
              onClick={() => { void controller.captureMoment(); }}
            >
              <Camera />
              <span><strong>Capture moment</strong><small>Alt+V</small></span>
            </button>
            <button
              type="button"
              aria-disabled={!openSegment ? 'true' : undefined}
              disabled={busy || stopping}
              onClick={() => { void controller.finishSegment(); }}
            >
              <Check />
              <span><strong>Finish ticket</strong><small>Alt+X</small></span>
            </button>
          </div>
        </div>

        <button
          type="button"
          className="vd-recorder-control__stop"
          disabled={starting || stopping}
          onClick={() => { void controller.stopRecording(); }}
        >
          <CircleStop /> {stopping ? 'Saving recording' : 'Stop recording'}
        </button>

        <p className="vd-recorder-control__hint">
          Shortcuts work while this recorder window is focused.
        </p>

        {snapshot.notice && (
          <div
            key={snapshot.notice.id}
            className="vd-recorder-control__toast"
            data-tone={snapshot.notice.tone}
            role={snapshot.notice.tone === 'error' ? 'alert' : 'status'}
            aria-live={snapshot.notice.tone === 'error' ? 'assertive' : 'polite'}
          >
            {snapshot.notice.message}
          </div>
        )}
      </div>
    </FloatingControlShell>
  );

  return shell;
}

export function RecorderPipPortal({
  pip,
  controller,
  snapshot,
  elapsedMs,
  stopping,
  recorderPhase = 'recording',
  snapshotUploads,
  onRetrySnapshot,
}: {
  pip: DocumentPipSnapshot;
  controller: RecordingSegmentController;
  snapshot: RecordingSegmentSnapshot;
  elapsedMs: number;
  stopping: boolean;
  recorderPhase?: RecorderPhase;
  snapshotUploads?: RecordingSnapshotUploadSnapshot;
  onRetrySnapshot?: (key: string) => void;
}) {
  const root = pip.pipWindow?.document.getElementById('vd-recorder-pip-root');
  if (pip.status !== 'open' || !root) return null;
  return createPortal(
    <RecorderFloatingControl
      controller={controller}
      snapshot={snapshot}
      elapsedMs={elapsedMs}
      stopping={stopping}
      recorderPhase={recorderPhase}
      inPictureInPicture
      snapshotUploads={snapshotUploads}
      onRetrySnapshot={onRetrySnapshot}
    />,
    root,
  );
}

/** Keeps the same live control available app-wide in PiP or in-tab fallback. */
export function RecorderControlHost({
  pip,
  controller,
  snapshot,
  elapsedMs,
  recorderPhase,
  snapshotUploads,
  onRetrySnapshot,
}: {
  pip: DocumentPipSnapshot;
  controller: RecordingSegmentController;
  snapshot: RecordingSegmentSnapshot;
  elapsedMs: number;
  recorderPhase: RecorderPhase;
  snapshotUploads?: RecordingSnapshotUploadSnapshot;
  onRetrySnapshot?: (key: string) => void;
}) {
  const active = [
    'requesting-display',
    'requesting-microphone',
    'starting',
    'recording',
    'stopping',
  ].includes(recorderPhase);
  if (!active) return null;
  const stopping = recorderPhase === 'stopping';
  return (
    <>
      <RecorderPipPortal
        pip={pip}
        controller={controller}
        snapshot={snapshot}
        elapsedMs={elapsedMs}
        stopping={stopping}
        recorderPhase={recorderPhase}
        snapshotUploads={snapshotUploads}
        onRetrySnapshot={onRetrySnapshot}
      />
      {pip.status !== 'open' && (
        <RecorderFloatingControl
          controller={controller}
          snapshot={snapshot}
          elapsedMs={elapsedMs}
          stopping={stopping}
          recorderPhase={recorderPhase}
          snapshotUploads={snapshotUploads}
          onRetrySnapshot={onRetrySnapshot}
        />
      )}
    </>
  );
}
